<script setup>
// The "no limits" graphic on /features. Each row is one thing platforms cap:
// a short bar that stops at a cap, the way a plan tier does, next to a Tap
// Leagues bar that runs off the edge of the card. The bars grow in when the
// card scrolls into view. Pure CSS; the figures are illustrative of the market,
// not any one product, so no competitor is named.
const ROWS = [
  {label: 'Members', cap: 'capped at 100', width: 28},
  {label: 'Admins', cap: 'capped at 3', width: 14},
  {label: 'Competitions', cap: 'capped at 2', width: 12},
  {label: 'Divisions', cap: 'capped per plan', width: 34},
  {label: 'Seasons', cap: 'capped per plan', width: 40},
];

const host = ref(null);
const shown = ref(false);
let observer = null;
onMounted(() => {
  if (!('IntersectionObserver' in window)) {
    shown.value = true;
    return;
  }
  observer = new IntersectionObserver((entries) => {
    if (entries.some(e => e.isIntersecting)) {
      shown.value = true;
      observer.disconnect();
    }
  }, {threshold: 0.35});
  observer.observe(host.value);
});
onUnmounted(() => observer?.disconnect());
</script>

<template>
  <div ref="host" class="flg tl-card" :class="{'flg--shown': shown}" aria-hidden="true">
    <div class="flg-head">
      <span class="flg-col flg-col--them">Typical plan</span>
      <span class="flg-col flg-col--us"><img src="/images/logo.png" alt="Tap Leagues" class="flg-logo"></span>
    </div>
    <div v-for="(row, i) in ROWS" :key="row.label" class="flg-row" :style="{'--i': i}">
      <span class="flg-label">{{ row.label }}</span>
      <span class="flg-track flg-track--them">
        <span class="flg-bar flg-bar--them" :style="{'--w': row.width + '%'}"/>
        <span class="flg-cap"><v-icon size="13" icon="mdi-lock-outline"/>{{ row.cap }}</span>
      </span>
      <span class="flg-track flg-track--us">
        <span class="flg-bar flg-bar--us"/>
        <span class="flg-inf">∞</span>
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Rows carry the side padding, so their hairlines run edge to edge */
.flg {
  position: relative;
  overflow: hidden;
  padding: 10px 0 4px;
}

/* Cells carry the padding, so the column lines run the full height of every row */
.flg-head {
  display: grid;
  grid-template-columns: 92px minmax(0, 1.1fr) minmax(0, 0.9fr);
  padding: 0 18px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  @media (min-width: 760px) {
    grid-template-columns: 120px minmax(0, 1fr) minmax(0, 1.2fr);
    padding: 0 22px;
  }
}

.flg-col {
  grid-column: 2;
  padding: 6px 0 10px 14px;
  border-left: 1px solid rgb(var(--v-theme-borderLight));
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));

  &--us {
    grid-column: 3;
    display: flex;
    align-items: flex-end;
  }
}

.flg-logo {
  display: block;
  height: 16px;
  width: auto;
}

.flg-row {
  display: grid;
  grid-template-columns: 92px minmax(0, 1.1fr) minmax(0, 0.9fr);
  align-items: stretch;
  padding: 0 18px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:last-child {
    border-bottom: 0;
  }

  @media (min-width: 760px) {
    grid-template-columns: 120px minmax(0, 1fr) minmax(0, 1.2fr);
    padding: 0 22px;
  }
}

.flg-label {
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 0;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: -0.2px;

  @media (min-width: 760px) {
    padding: 10px 12px 10px 0;
    font-size: 13px;
  }
}

.flg-track {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 42px;
  min-width: 0;
  padding: 8px 0 8px 10px;
  border-left: 1px solid rgb(var(--v-theme-borderLight));

  @media (min-width: 760px) {
    padding: 10px 0 10px 14px;
  }

  /* The cap sits beside the bar when there is room, under it on a phone */
  &--them {
    padding-right: 8px;

    @media (max-width: 759px) {
      padding-right: 4px;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: 5px;
    }
  }
}

.flg-bar {
  display: block;
  height: 12px;
  border-radius: 999px;
  transform-origin: left center;
  transform: scaleX(0);
  transition: transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1);
  transition-delay: calc(var(--i) * 90ms);

  &--them {
    width: var(--w);
    min-width: 18px;
    background: rgb(var(--v-theme-border));
  }

  /* The full track, the same bar as the capped one, with no cap on it */
  &--us {
    width: 100%;
    background: rgb(var(--v-theme-primary));
  }
}

.flg--shown .flg-bar {
  transform: scaleX(1);
}

.flg-cap {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  color: rgb(var(--v-theme-charcoal));
  opacity: 0;
  transition: opacity 0.4s ease;
  transition-delay: calc(var(--i) * 90ms + 500ms);

  @media (max-width: 759px) {
    margin-left: 0;
    font-size: 10.5px;
    letter-spacing: -0.1px;
  }
}

.flg--shown .flg-cap {
  opacity: 1;
}

.flg-inf {
  position: absolute;
  right: 8px;
  top: 50%;
  margin-top: -7px;
  font-size: 15px;
  font-weight: 800;
  line-height: 14px;
  color: #fff;
  opacity: 0;
  transform: translateX(8px);
  transition: opacity 0.4s ease, transform 0.4s ease;
  transition-delay: calc(var(--i) * 90ms + 600ms);
}

.flg--shown .flg-inf {
  opacity: 1;
  transform: none;
}
</style>
